import { ReactNode, useCallback, useRef, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog/ConfirmDialog";

type ConfirmOptions = {
  cancelLabel: string;
  className?: string;
  confirmLabel: string;
  message: ReactNode | string;
  subTitle?: string;
  title?: string;
};

type UseConfirmOptions = {
  autoClose?: boolean;
};

export function useConfirm({ autoClose = true }: UseConfirmOptions = {}) {
  const [state, setState] = useState<{
    options: ConfirmOptions;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise((resolve) => {
      resolver.current = resolve;
      setState({ options });
    });
  }, []);

  const close = useCallback(() => {
    setLoading(false);
    setState(null);
  }, []);

  const handle = useCallback(
    (result: boolean) => {
      resolver.current?.(result);
      if (autoClose) {
        setState(null);
      } else if (result) {
        setLoading(true);
      }
    },
    [autoClose],
  );

  const dialog = state ? (
    <ConfirmDialog
      {...state.options}
      loading={loading}
      onCancel={() => handle(false)}
      onConfirm={() => handle(true)}
      open
    />
  ) : null;

  return [confirm, dialog, close] as const;
}
